import AudioPlayerContainer from "@features/session/containers/AudioPlayerContainer";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import DialogWrapper from "@memorang/ui/components/DialogWrapper";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, View } from "react-native";
import { Checkbox, Dialog, List, Text } from "react-native-paper";

const AUDIO_URI = `${process.env.EXPO_PUBLIC_SUPABASE_URL}/storage/v1/object/public/memorang-assets/partners/toefl/tts-audio/tts_test-audio_1738156059281.mp3`;

type Props = {
	showDialog: boolean;
	handleClose: () => void;
};

export const AudioPlaybackTestDialog = ({ showDialog, handleClose }: Props) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const showAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.showAudioPlaybackTestDialog,
	);
	const toggleAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.toggleAudioPlaybackTestDialog,
	);

	const appName = useAppStore((state) => state.app.name);

	return (
		<DialogWrapper visible={showDialog} handleClose={handleClose}>
			<Dialog.Icon icon="music-note-eighth" />
			<Dialog.Title style={{ textAlign: "center" }}>
				{t("session.audioPlaybackTestDialog.title")}
			</Dialog.Title>
			<Dialog.Content
				style={{
					gap: 16,
				}}
			>
				<Text variant="bodyMedium">
					{t("session.audioPlaybackTestDialog.description", {
						appName,
					})}
				</Text>
				<AudioPlayerContainer uri={AUDIO_URI} />
				<CustomMarkdownRenderer
					text={t("session.audioPlaybackTestDialog.textToHear", {
						text: "_**This is a test playback to ensure audio playback is audible.**_",
					})}
				/>
				<Box>
					<List.Accordion
						title={t("session.audioPlaybackTestDialog.troubleshooting.title")}
						titleNumberOfLines={2}
						expanded={expanded}
						left={(props) => (
							<List.Icon {...props} icon="alert-circle-outline" />
						)}
						onPress={() => setExpanded(!expanded)}
						style={{ padding: 0 }}
					>
						<List.Item
							title={t(
								"session.audioPlaybackTestDialog.troubleshooting.volumeTitle",
							)}
							left={(props) => <List.Icon {...props} icon="volume-plus" />}
							description={
								isWeb
									? t(
											"session.audioPlaybackTestDialog.troubleshooting.volumeDescriptionWeb",
										)
									: t(
											"session.audioPlaybackTestDialog.troubleshooting.volumeDescriptionMobile",
										)
							}
							titleNumberOfLines={2}
						/>
						<List.Item
							left={(props) => <List.Icon {...props} icon="headphones" />}
							title={t(
								"session.audioPlaybackTestDialog.troubleshooting.outputTitle",
							)}
							description={t(
								"session.audioPlaybackTestDialog.troubleshooting.outputDescription",
							)}
							titleNumberOfLines={2}
						/>
						{isWeb && (
							<List.Item
								left={(props) => <List.Icon {...props} icon="volume-off" />}
								title={t(
									"session.audioPlaybackTestDialog.troubleshooting.browserTitle",
								)}
								description={t(
									"session.audioPlaybackTestDialog.troubleshooting.browserDescription",
								)}
								titleNumberOfLines={2}
							/>
						)}
					</List.Accordion>
				</Box>
				<View style={{ flexDirection: "row", alignItems: "center" }}>
					<Checkbox.Android
						status={showAudioPlaybackTestDialog ? "unchecked" : "checked"}
						onPress={toggleAudioPlaybackTestDialog}
					/>
					<Pressable onPress={toggleAudioPlaybackTestDialog}>
						<Text>{t("session.audioPlaybackTestDialog.skipCheck")}</Text>
					</Pressable>
				</View>
			</Dialog.Content>
			<Dialog.Actions>
				<Button mode="contained" onPress={handleClose}>
					{t("session.audioPlaybackTestDialog.workingButton")}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};
